import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { LuX, LuSettings } from 'react-icons/lu';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Tag, type TagColor } from '@/components/Tag';
import useCandidateProfileTags from '../hooks/useCandidateProfileTags';
import useBulkAddTags from '../api/useBulkAddTags';
import useBulkRemoveTags from '../api/useBulkRemoveTags';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  ids: number[];
  mode: 'add' | 'remove';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export default function BulkTagModal({ ids, mode, open, onOpenChange, onSuccess }: Props) {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const { data } = useCandidateProfileTags();
  const { mutateAsync: bulkAddTags, isPending: isAdding } = useBulkAddTags();
  const { mutateAsync: bulkRemoveTags, isPending: isRemoving } = useBulkRemoveTags();

  const isPending = isAdding || isRemoving;
  const title = mode === 'add' ? '追加するタグを選択' : '解除するタグを選択';
  const submitLabel = mode === 'add' ? '追加する' : '解除する';

  const handleTagToggle = (tagId: number, checked: boolean) => {
    setSelectedTagIds((prev) =>
      checked ? (prev.includes(tagId) ? prev : [...prev, tagId]) : prev.filter((id) => id !== tagId)
    );
  };

  const handleDeselectAll = () => {
    setSelectedTagIds([]);
  };

  const handleSubmit = async () => {
    if (selectedTagIds.length === 0) return;
    try {
      const params = { ids, custom_tag_ids: selectedTagIds };
      if (mode === 'add') {
        await bulkAddTags(params);
        showSuccessToast('タグを追加しました');
      } else {
        await bulkRemoveTags(params);
        showSuccessToast('タグを解除しました');
      }
      setSelectedTagIds([]);
      onSuccess();
    } catch {
      showErrorToast(mode === 'add' ? 'タグの追加に失敗しました' : 'タグの解除に失敗しました');
    }
  };

  const handleClose = () => {
    setSelectedTagIds([]);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[860px]! max-w-[860px]!">
        {/* ヘッダー */}
        <div className="flex items-center justify-between border-b border-solid border-border px-lg py-md">
          <p className="text-base font-bold text-main">{title}</p>
          {selectedTagIds.length > 0 && (
            <button
              type="button"
              className="flex cursor-pointer items-center gap-xxs border-none bg-transparent text-sm font-bold text-primary"
              onClick={handleDeselectAll}
            >
              <LuX className="size-4" />
              全ての選択を解除
            </button>
          )}
        </div>

        {/* タグ選択エリア */}
        <div className="p-lg">
          {data?.tags && (
            <div className="flex max-h-[300px] flex-wrap gap-x-xl gap-y-md overflow-y-auto">
              {data.tags.map((tag) => (
                <Checkbox
                  key={tag.id}
                  value={tag.id}
                  checked={selectedTagIds.includes(tag.id)}
                  onChange={(checked) => handleTagToggle(tag.id, checked)}
                >
                  <Tag color={tag.colorHex as TagColor}>{tag.name}</Tag>
                </Checkbox>
              ))}
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="flex items-center justify-between border-t border-solid border-border px-lg py-md">
          <Button color="tertiary" size="sm" asChild>
            <Link to="/agent/candidate_tags" onClick={handleClose}>
              <LuSettings className="size-4 text-primary" />
              <span className="text-sm font-bold text-primary">タグを管理</span>
            </Link>
          </Button>
          <div className="flex items-center gap-md">
            <Button type="button" color="secondary" size="md" onClick={handleClose}>
              キャンセル
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={handleSubmit}
              disabled={selectedTagIds.length === 0 || isPending}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
